import {
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import {
  CheckCircle2Icon,
  CoinsIcon,
  InfoIcon,
  MapPinIcon,
  PencilIcon,
  ShoppingBagIcon,
} from "lucide-react";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import { formatTimeAgo } from "@/shared/lib/date-time";
import { RowActions } from "@/shared/ui/RowActions";
import type { ShopItem } from "../api/client";

type ShopsTableProps = {
  data: ShopItem[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onView?: (id: string) => void;
  onVerify?: (id: string) => void;
  onBranches?: (id: string) => void;
  onFeeSettings?: (id: string) => void;
};

const STATUS_STYLE: Record<
  string,
  { dot: string; text: string; label: string }
> = {
  active: {
    dot: "bg-green-500",
    text: "text-green-600 dark:text-green-500",
    label: "ໃຊ້ງານປົກກະຕິ",
  },
  suspended: {
    dot: "bg-red-500",
    text: "text-red-600 dark:text-red-500",
    label: "ລະງັບ",
  },
  unverified: {
    dot: "bg-[#F26522]",
    text: "text-[#F26522]",
    label: "ລໍຖ້າຢືນຢັນ",
  },
};

const VERIFIED_LABELS: Record<string, string> = {
  none: "ຍັງບໍ່ຢືນຢັນ",
  basic: "ພື້ນຖານ",
  verified: "ຢືນຢັນແລ້ວ",
};

function shopCode(id: string): string {
  const short = id.replace(/-/g, "").slice(-8).toUpperCase();
  return `SH-${short}`;
}

export function ShopsTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onView,
  onVerify,
  onBranches,
  onFeeSettings,
}: ShopsTableProps) {
  const { has } = usePermissions();
  const canBranches =
    has(Permissions.shops.read) || has(Permissions.branches.list);
  const canFeeRules = has(Permissions.fee_rules.list);

  const columns: TanstackReactTable.ColumnDef<ShopItem>[] = [
    {
      id: "name",
      header: "ຊື່ຮ້ານ & ລະຫັດ",
      cell: ({ row }: { row: { original: ShopItem } }) => {
        const shop = row.original;
        const id = shop.id as string;
        return (
          <div className="flex items-center gap-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-[#004799]/10">
              <ShoppingBagIcon className="size-5 text-[#004799]" />
            </div>
            <div className="min-w-0">
              <div className="truncate font-medium">{shop.name}</div>
              <div className="text-muted-foreground text-xs">
                {shopCode(id)}
              </div>
            </div>
          </div>
        );
      },
      size: 200,
    },
    {
      id: "status",
      header: "ສະຖານະ",
      cell: ({ row }) => {
        const status = row.original.status ?? "unverified";
        const style = STATUS_STYLE[status] ?? STATUS_STYLE.unverified;
        return (
          <div className="flex items-center gap-2">
            <span
              className={`size-2 shrink-0 rounded-full ${style.dot}`}
              aria-hidden
            />
            <span className={style.text}>{style.label}</span>
          </div>
        );
      },
      size: 140,
    },
    {
      id: "verifiedLevel",
      header: "ລະດັບການຢືນຢັນ",
      cell: ({ row }) => {
        const level = row.original.verifiedLevel ?? "none";
        const isVerified = level === "verified";
        return (
          <div className="flex items-center gap-2">
            {isVerified ? (
              <CheckCircle2Icon className="size-4 shrink-0 text-[#004799]" />
            ) : (
              <InfoIcon className="size-4 shrink-0 text-muted-foreground" />
            )}
            <span
              className={
                isVerified
                  ? "text-[#004799] text-sm"
                  : "text-muted-foreground text-sm"
              }
            >
              {VERIFIED_LABELS[level] ?? level}
            </span>
          </div>
        );
      },
      size: 140,
    },
    {
      id: "branchCount",
      header: "ສາຂາ",
      cell: ({ row }) => (
        <span className="text-sm">{row.original.branchCount ?? 0}</span>
      ),
      size: 80,
    },
    createSortableColumn<ShopItem>("updatedAt", "ເຄື່ອງຢູ່ຫຼ້າສຸດ", {
      size: 120,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.updatedAt ? formatTimeAgo(row.original.updatedAt) : "-"}
        </span>
      ),
    }),
    {
      id: "actions",
      cell: ({ row }: { row: { original: ShopItem } }) => {
        if (!has(Permissions.shops.list) && !has(Permissions.shops.read))
          return null;
        const actions = [];
        if (has(Permissions.shops.update) && onView) {
          actions.push({
            label: "ແກ້ໄຂ",
            icon: <PencilIcon className="h-4 w-4" />,
            onClick: () => onView(row.original.id as string),
          });
        }
        if (canBranches && onBranches) {
          actions.push({
            label: "ຈັດການສາຂາ",
            icon: <MapPinIcon className="h-4 w-4" />,
            onClick: () => onBranches(row.original.id as string),
          });
        }
        if (has(Permissions.shops.verify) && onVerify) {
          actions.push({
            label: "ຢືນຢັນ",
            icon: <CheckCircle2Icon className="h-4 w-4" />,
            onClick: () => onVerify(row.original.id as string),
          });
        }
        if (canFeeRules && onFeeSettings) {
          actions.push({
            label: "ຕັ້ງຄ່າຄ່າທຳນຽມ",
            icon: <CoinsIcon className="h-4 w-4" />,
            onClick: () => onFeeSettings(row.original.id as string),
          });
        }
        return <RowActions actions={actions} maxInline={4} />;
      },
      size: 120,
    },
  ];

  return (
    <DataTable<ShopItem, unknown>
      noDataMessage="ບໍ່ພົບຮ້ານຄ້າ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
    />
  );
}
